import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useMemo } from 'react';
import { cn } from '@repo/ux/lib/utils';

type SafeHtmlProps = Readonly<{
  html: string | null | undefined;
  className?: string;
}>;

/**
 * Renders trusted-after-sanitization HTML (event descriptions, agreements,
 * training copy) with consistent prose styling. Always sanitize through this
 * component — never call `html-react-parser` or `dangerouslySetInnerHTML`
 * on CMS-provided markup directly.
 */
export function SafeHtml({ html, className }: SafeHtmlProps) {
  const sanitizedHtml = useMemo(() => {
    if (!html) return '';
    return DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
    });
  }, [html]);

  if (!sanitizedHtml) return null;

  return (
    <div
      className={cn(
        'text-left text-base leading-7 text-foreground/80 [&_a]:font-medium [&_a]:text-brand-primary [&_a]:underline [&_li]:my-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6',
        className
      )}
    >
      {parse(sanitizedHtml)}
    </div>
  );
}
